import React, { useState, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import countryList from "react-select-country-list";
import { useForm } from "react-hook-form";
import { useCreateLeague, useInviteToLeague } from "../api/leaguesApi";
import posthog from 'posthog-js';
import {
  ALLOWED_LEAGUE_AVATAR_TYPES,
  LEAGUE_AVATAR_ACCEPT,
  MAX_LEAGUE_AVATAR_SIZE_BYTES
} from "../utils/leagueAvatar";
import { getOwnerJoinabilityErrorMessage } from "../utils/inviteErrors";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseInviteEmails(raw) {
  if (!raw) return { valid: [], invalid: [] };
  const items = raw
    .split(/[,\n;]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const valid = [];
  const invalid = [];
  for (const item of items) {
    if (EMAIL_REGEX.test(item)) valid.push(item);
    else invalid.push(item);
  }
  return { valid, invalid };
}

function CreateLeague() {
  const createLeague = useCreateLeague();
  const inviteToLeague = useInviteToLeague();
  const [inviteEmails, setInviteEmails] = useState("");

  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const inputRef = useRef(null);
  const imageReadTokenRef = useRef(0);

  const {
    register,
    handleSubmit: validateSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      isPrivate: false,
      amountOfTeams: "",
    },
  });
  const isPrivateChecked = watch("isPrivate");

  const resetImageSelection = (inputElement = inputRef.current) => {
    imageReadTokenRef.current += 1;
    setImage(null);
    setImageFile(null);
    if (inputElement) {
      inputElement.value = "";
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      resetImageSelection(e.target);
      return;
    }

    if (!ALLOWED_LEAGUE_AVATAR_TYPES.has(file.type)) {
      alert("League avatar must be a JPG or PNG image");
      resetImageSelection(e.target);
      return;
    }

    if (file.size > MAX_LEAGUE_AVATAR_SIZE_BYTES) {
      alert("League avatar must be 5 MB or smaller");
      resetImageSelection(e.target);
      return;
    }

    const readToken = imageReadTokenRef.current + 1;
    imageReadTokenRef.current = readToken;
    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      if (imageReadTokenRef.current === readToken) {
        setImage(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleClearImage = () => {
    resetImageSelection();
  };

  const [startDate, setStartDate] = useState(new Date());

  const [selectedOption, setSelectedOption] = useState("");

  const handleChangeAmount = (event) => {
    const value = event.target.value;
    setSelectedOption(value);
    setValue("amountOfTeams", value);
  };

  const teamsAmount = [4, 6, 8, 12, 14, 16, 18, 20];

  const radioTeamsAmount = teamsAmount.map(function (value, index) {
    return (
      <div key={index} className="checkbox-block round">
        <input
          type="radio"
          id={`teams_amount_${value}`}
          name="teams-amount"
          value={value}
          checked={selectedOption === value.toString()}
          onChange={handleChangeAmount}
        />
        <label htmlFor={`teams_amount_${value}`}>{value}</label>
      </div>
    );
  });

  // Add your form validation
  const onSubmit = async (data) => {
    try {
      // Check that the number of teams is selected
      if (!data.amountOfTeams) {
        alert('Please select the amount of teams');
        return;
      }

      // Check if league name exists
      if (!data.leagueName) {
        alert('Please enter league name');
        return;
      }

      // Check if a password is provided for private leagues
      if (!data.isPrivate && (!data.password || data.password.length < 4)) {
        alert('Private leagues require a password of at least 4 characters');
        return;
      }

      const formData = new FormData();
      
      // Add basic data
      formData.append('name', data.leagueName);
      formData.append('isPrivate', data.isPrivate ? 'false' : 'true');
      formData.append('freeJoin', data.isPrivate ? 'true' : 'false');
      formData.append('amountOfTeams', data.amountOfTeams);

      // Add password for private league
      if (!data.isPrivate && data.password) {
        formData.append('password', data.password);
      }

      // Add image if it exists
      if (imageFile) {
        formData.append('image', imageFile);
      }

      // Log data for debugging
      console.log('Sending data:', {
        name: data.leagueName,
        isPrivate: data.isPrivate ? 'false' : 'true',
        freeJoin: data.isPrivate ? 'true' : 'false',
        amountOfTeams: data.amountOfTeams,
        hasPassword: !data.isPrivate && !!data.password,
        hasImage: !!imageFile
      });

      // Add detailed logging for form submission values
      console.log('Form submission values:', {
        isPrivateCheckbox: data.isPrivate, // Raw checkbox value
        isPrivateFormData: data.isPrivate ? 'false' : 'true', // Value sent to backend
        freeJoinFormData: data.isPrivate ? 'true' : 'false', // Value sent to backend
        password: data.password ? '***' : 'not provided', // Just log if password exists
        passwordRequired: !data.isPrivate // Whether password is required
      });

      const response = await createLeague.mutateAsync(formData);
      console.log('League created - full response:', response);
      posthog.capture('league_created', {
        league_id: response?.league?.id,
        league_name: data.leagueName,
        is_private: !data.isPrivate,
        amount_of_teams: data.amountOfTeams,
      });

      // Optional: fire invites entered in the "Invite People" textarea.
      // Non-fatal — a failed invite must not lose the freshly-created league.
      const { valid: inviteValid, invalid: inviteInvalid } = parseInviteEmails(inviteEmails);
      if (inviteInvalid.length > 0) {
        alert(`Skipped invalid email(s): ${inviteInvalid.join(', ')}`);
      }
      if (inviteValid.length > 0 && response?.league?.id) {
        try {
          const inviteResult = await inviteToLeague.mutateAsync({
            leagueId: response.league.id,
            emails: inviteValid,
            slug: response.league.slug,
          });
          const sent = inviteResult?.results?.successful?.length ?? 0;
          const failed = inviteResult?.results?.failed?.length ?? 0;
          posthog.capture('league_invite_sent', { league_id: response.league.id, invites_sent: sent, invites_failed: failed });
          if (failed > 0) {
            alert(`League created. Sent ${sent} invite(s), ${failed} failed.`);
          }
        } catch (inviteErr) {
          console.error('Invite send failed after league create:', inviteErr);
          const msg = getOwnerJoinabilityErrorMessage(inviteErr) || inviteErr?.response?.data?.error || 'Could not send invites — you can retry from League Settings.';
          alert(`League created, but invites failed: ${msg}`);
        }
      }

      // Redirect to the page specified in the server response
      if (response.redirectUrl) {
        window.location.href = response.redirectUrl;
      } else {
        // If redirectUrl is not specified, redirect to the league table page
        // We need the league slug from the response
        if (response.league && response.league.slug) {
          window.location.href = `/league/${response.league.slug}/table`;
        } else {
          // Fallback to matches page if we don't have a slug
          window.location.href = '/matches';
        }
      }
    } catch (error) {
      console.error('Error:', error);
      // Surface meaningful backend message if available
      const backendMsg = error?.response?.data?.error || error.message || 'Failed to create league';
      alert(backendMsg);
    }
  };

  const handleSubmit = validateSubmit(onSubmit);

  return (
    <div className="page-login">
      <div className="wrapper">
        <h1>CREATE LEAGUE</h1>

        <div className="form-block">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="i1">LEAGUE NAME *</label>
              <input
                id="leagueName"
                type="text"
                {...register("leagueName", {
                  required: "League Name is required",
                })}
              />
              {errors.leagueName && (
                <div
                  style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                >
                  {errors.leagueName.message}
                </div>
              )}
            </div>

            <div className="form-group photo-group">
              <div className="photo-grid">
                <div className="upload-block">
                  <h4>UPLOAD AVATAR <span style={{fontWeight:'normal',fontSize:'12px'}}>(max 5 MB)</span></h4>
                  <label htmlFor="photo_input" className="read-more-btn">
                    CHOOSE FILE
                  </label>
                  <input
                    ref={inputRef}
                    id="photo_input"
                    accept={LEAGUE_AVATAR_ACCEPT}
                    type="file"
                    onChange={handleImageChange}
                  />
                </div>
                <div className="file-tab">
                  <div className={`tab file-block ${image ? "active" : ""}`}>
                    <div className="image">
                      <div
                        className="image-clear-btn"
                        onClick={() => {
                          handleClearImage();
                        }}
                      />
                      <img src={image} id="file_preview" alt="Preview" />
                    </div>
                  </div>
                  <div className="tab default-block" />
                </div>
              </div>
            </div>

            <div className="form-group">
              <h4>AMOUNT OF TEAMS</h4>
              <div className="radio-container">{radioTeamsAmount}</div>
            </div>

            <div className="form-group">
              <label htmlFor="i2">INVITE PEOPLE</label>
              <textarea
                id="i2"
                cols="30"
                rows="10"
                placeholder="Enter E-mail addresses here, separated by a comma"
                value={inviteEmails}
                onChange={(e) => setInviteEmails(e.target.value)}
              />
            </div>

            <div className="checkbox-block form-group round">
              <input
                type="checkbox"
                id="isPrivate"
                {...register("isPrivate")}
              />
              <label htmlFor="isPrivate">ANY USERS CAN JOIN (PUBLIC LEAGUE)</label>
              {errors.isPrivate && (
                <div
                  style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                >
                  {errors.isPrivate.message}
                </div>
              )}
            </div>

            {/* Password is required for private leagues (when "ANY USERS CAN JOIN" is unchecked) */}

            {!isPrivateChecked && <div className="form-group">
              <label htmlFor="p1">PASSWORD (REQUIRED FOR PRIVATE LEAGUES)</label>
              <input
                id="password"
                type="text"
                {...register("password", {
                  required: "Password is required for private leagues",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
              />
              {errors.password && (
                <div
                  style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                >
                  {errors.password.message}
                </div>
              )}
            </div>}

            <button type="submit" className="submit-btn">
              CREATE AND SEND INVITES
            </button>
          </form>

          <div className="descr">* Required field</div>
        </div>
      </div>
    </div>
  );
}

export { CreateLeague };
